import { BrandKitStepProps } from 'components/FirstTimeExperienceModal/Types'
import React from 'react'
import Styles from './BrandKitFinished.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'blaze-ui/icons'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { generatePath } from 'react-router-dom'
import { BRAND_KIT_PATH, WORKSPACE_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useOnboard } from 'hooks/queries/useOnboard'
import useURLParams from 'hooks/useURLParams'
import { BrandKit } from 'types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { sendMessageToNativeApp } from 'utils/sendMessageToNativeApp'
import { BrandKitPreviewCard } from '../BrandKitColorsAndFonts/BrandKitPreviewCard/BrandKitPreviewCard'
import { FontPairing } from 'constants/fontPairings'
import { Text } from 'blaze-ui/Text/Text'

interface Props extends BrandKitStepProps {
  onOpenBrandKit?: (brandKit: BrandKit) => void
  title?: string
  showHomeButton?: boolean
  selectedFontPairing: FontPairing
}

export const BrandKitFinished: React.FC<Props> = ({
  onNext,
  onOpenBrandKit,
  brandKit,
  title,
  showHomeButton,
  selectedFontPairing,
}) => {
  const colors: string[] = brandKit.brand_visual_kit?.primary_color_palette.colors.map((color) => color.hex) || []
  const workspaceId = useCurrentWorkspaceId()
  const openWindowSafely = useOpenWindowSafely()
  const { mutateAsync: onboard } = useOnboard()
  const [, setURLParams] = useURLParams()
  const { isIOSNative, isMobile } = useResponsiveDesign()

  const handleOpenBrandKit = async () => {
    if (onOpenBrandKit) {
      return onOpenBrandKit(brandKit)
    }

    const url = generatePath(BRAND_KIT_PATH, { workspaceId, id: brandKit.id })
    await onboard()

    if (url) {
      openWindowSafely(url)
    }
  }

  const handleNext = () => {
    if (isIOSNative) {
      sendMessageToNativeApp('brandKitCreateContent')
    } else {
      const path = generatePath(WORKSPACE_PATH, { workspaceId })
      const pathWithQuery = `${path}?modal=create-new`
      setURLParams({ redirectTo: pathWithQuery })
      onNext()
    }
  }

  const handleHome = () => {
    if (isIOSNative) {
      sendMessageToNativeApp('brandKitGoHome')
    }
  }

  return (
    <div className={Styles.container}>
      {title && <h1 className={Styles.header}>{title}</h1>}
      <BrandKitPreviewCard brandKit={brandKit} colors={colors} fontPairing={selectedFontPairing} />
      <div className={Styles.buttonsAndText}>
        <div className={Styles.text}>
          <Heading level={4}>Your Brand Kit is ready to go. </Heading>
          <Text>Start creating or review your kit to make edits.</Text>
        </div>
        {showHomeButton && (
          <Button size="xl" variant="tertiary" onPress={handleHome} fullWidth>
            Go to Home
          </Button>
        )}
        <div className={Styles.buttons}>
          {!isIOSNative && (
            <Button size={isMobile ? 'xl' : 'lg'} endIcon={ArrowRight} onPress={handleNext} fullWidth>
              {isMobile ? 'Create some content with it!' : 'Create Content'}
            </Button>
          )}
          <Button
            variant="secondary"
            size={isMobile ? 'xl' : 'lg'}
            fullWidth
            onPress={handleOpenBrandKit}
            className={Styles.nextButton}
          >
            Open Brand Kit
          </Button>
        </div>
      </div>
    </div>
  )
}
